<script>
  import { format } from 'svelte-i18n';
  import { Button, Modal, ModalBody, ModalFooter } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { ImageSingleUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  export let show = false;
  export let showPreviousModal = false;
  export let refetch = false;

  let fileData = {};
  let files = [];
  let inline = false;
  let uploadError = false;
  let isUpdating = false;
  let fileSizeWarning = false;

  const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB in bytes

  const onGoBack = () => {
    onClose();

    setTimeout(() => (showPreviousModal = true), 200);
  };

  const onClose = () => {
    fileData = {};
    files = [];
    inline = false;
    uploadError = false;
    fileSizeWarning = false;

    show = false;
  };

  const onKeyUp = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const onRemoveFile = () => {
    files = [];
    fileData = {};
    uploadError = false;
    fileSizeWarning = false;
  };

  const onFileChange = () => {
    uploadError = false;
    fileSizeWarning = false;

    if (files.length > 0 && files[0].size > MAX_FILE_SIZE) {
      fileSizeWarning = true;
    }
  };

  /** @returns {Promise<boolean>} whether the file landed with a usable filename. */
  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    const { hash, filesize, filename, metadata } = response ?? {};

    if (response?.status >= 400 || !filename) {
      isUpdating = false;
      uploadError = true;

      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }), 'error');

      return false;
    }

    fileData = { ...fileData, hash, filesize, filename, metadata: metadata ?? {} };

    return true;
  };

  const onUploadImage = async () => {
    isUpdating = true;

    // Without this the image is saved with no filename/hash and the
    // assets list cannot render it.
    if (!(await onFileUpload())) {
      return;
    }

    const data = {
      filename: fileData.filename,
      filesize: fileData.filesize,
      hash: fileData.hash,
      inline: inline,
      metadata: fileData.metadata,
      mimetype: fileData.metadata.mimetype,
      rulename: fileData.filename
    };

    const response = await AssetsService.addImage(data);

    isUpdating = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: fileData.filename } }), 'error');

      uploadError = true;
      return;
    }

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: fileData.filename } }));

    setTimeout(() => (show = false), 200);

    refetch = true;
  };

  $: if (!show) {
    onClose();
  }
</script>

<Modal bind:isOpen={show} close={false} size="lg" fullscreen="lg">
  <Loader backdrop show={isUpdating} />
  <ModalBody id="image-modal" class="image-form-modal">
    <div class="modal-actions">
      <button class="back" on:click={onGoBack}>
        <Icon name="arrow-left" />
        <span>{$format('label.BACK')}</span>
      </button>

      <button class="close" on:click={onClose}>
        <Icon name="x" />
      </button>
    </div>

    <div>
      <h1>{$format('label.IMAGE_SINGLE_MODAL_SINGLE_UPLOAD')}</h1>
      <p>{$format('label.DESCRIPTION.IMAGE_SINGLE_MODAL_SINGLE_UPLOAD')}</p>
    </div>

    <div class="modal-body">
      <ImageSingleUploadForm
        bind:files
        bind:inline
        bind:fileSizeWarning
        {onFileChange}
        {onRemoveFile}
        inputId="image-single"
      />
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="primary" on:click={onUploadImage} disabled={!files.length || uploadError}>
      {$format('label.IMAGE_SINGLE_MODAL_UPLOAD_IMAGE')}
    </Button>
    {#if uploadError}
      <small class="text-danger">
        {@html $format('label.IMAGE_SINGLE_MODAL_UPLOAD_ERROR', { values: { filename: fileData.filename } })}</small
      >
    {/if}
  </ModalFooter>
</Modal>

<svelte:window on:keyup={onKeyUp} />

<style lang="scss" src="./image-single-modal.scss"></style>
